import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  EyeIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Badge, Button, Card, CardHeader, InlineSpinner, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { lmsApi } from '@/api'
import { apiErrorMessage, saveBlob } from '@/api/lms'
import type { CertificatePlaceholder, CertificateTemplate } from '@/api/lms'

export default function AdminCertificateTemplatesPage() {
  const [templates, setTemplates] = useState<CertificateTemplate[]>([])
  const [placeholders, setPlaceholders] = useState<CertificatePlaceholder[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  // Upload / replace modal
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<CertificateTemplate | null>(null)
  const [form, setForm] = useState({ name: '', description: '', is_default: false })
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [toDelete, setToDelete] = useState<CertificateTemplate | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [templatesData, placeholdersData] = await Promise.all([
        lmsApi.admin.getCertificateTemplates(),
        lmsApi.admin.getCertificatePlaceholders(),
      ])
      setTemplates(templatesData)
      setPlaceholders(placeholdersData)
    } catch (err) {
      setError(await apiErrorMessage(err, 'Errore nel caricamento dei modelli.'))
    } finally {
      setLoading(false)
    }
  }

  const run = async (key: string, action: () => Promise<void>, fallback: string) => {
    setBusy(key)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(await apiErrorMessage(err, fallback))
    } finally {
      setBusy(null)
    }
  }

  const handlePreview = (template: CertificateTemplate) =>
    run(`preview-${template.id}`, async () => {
      const blob = await lmsApi.admin.previewCertificateTemplate(template.id)
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      window.open(url, '_blank')
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }, "Impossibile generare l'anteprima.")

  const handleDownload = (template: CertificateTemplate) =>
    run(`download-${template.id}`, async () => {
      saveBlob(await lmsApi.admin.downloadCertificateTemplate(template.id), template.file_name || 'modello.docx')
    }, 'Impossibile scaricare il modello.')

  const handleMakeDefault = (template: CertificateTemplate) =>
    run(`default-${template.id}`, async () => {
      await lmsApi.admin.updateCertificateTemplate(template.id, { is_default: true })
      await fetchData()
    }, 'Impossibile impostare il modello predefinito.')

  const handleDelete = () => {
    if (!toDelete) return
    const template = toDelete
    run(`delete-${template.id}`, async () => {
      await lmsApi.admin.deleteCertificateTemplate(template.id)
      setToDelete(null)
      await fetchData()
    }, 'Impossibile eliminare il modello.')
  }

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', description: '', is_default: templates.length === 0 })
    setFile(null)
    setFormError(null)
    setShowModal(true)
  }

  const openEdit = (template: CertificateTemplate) => {
    setEditing(template)
    setForm({ name: template.name, description: template.description, is_default: template.is_default })
    setFile(null)
    setFormError(null)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Indica un nome per il modello.')
      return
    }
    if (!editing && !file) {
      setFormError('Scegli il file Word (.docx) del modello.')
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editing) {
        await lmsApi.admin.updateCertificateTemplate(editing.id, {
          name: form.name,
          description: form.description,
          file: file || undefined,
          is_default: form.is_default && !editing.is_default,
        })
      } else {
        await lmsApi.admin.uploadCertificateTemplate({ ...form, file: file! })
      }
      setShowModal(false)
      await fetchData()
    } catch (err) {
      setFormError(await apiErrorMessage(err, 'Errore nel salvataggio del modello.'))
    } finally {
      setSaving(false)
    }
  }

  const copyTag = async (tag: string) => {
    try {
      await navigator.clipboard.writeText(tag)
      setCopied(tag)
      window.setTimeout(() => setCopied(null), 1500)
    } catch {
      // Clipboard not available: the tag is visible and can be copied by hand
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento modelli attestato..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link to="/gestione/courses" className="inline-flex items-center text-sm text-gray-500 hover:text-primary-600 mb-1">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Gestione corsi
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Modelli attestato</h1>
          <p className="text-gray-600">
            Modelli Word con segnaposto: a fine corso vengono compilati con i dati dello studente e convertiti in PDF.
          </p>
        </div>
        <Button onClick={openCreate}>
          <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
          Carica modello
        </Button>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 rounded-lg px-4 py-3">{error}</p>}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          {templates.length === 0 && (
            <Card className="p-8 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">
                Nessun modello caricato: finché non ne carichi uno gli attestati non possono essere rilasciati.
              </p>
            </Card>
          )}
          {templates.map((template) => (
            <Card key={template.id} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    {template.is_default && <Badge variant="success" size="sm">Predefinito</Badge>}
                  </div>
                  {template.description && <p className="text-sm text-gray-600 mt-1">{template.description}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    {template.file_name} · usato da {template.courses_count}{' '}
                    {template.courses_count === 1 ? 'corso' : 'corsi'}
                    {template.is_default ? ' (più tutti i corsi senza un modello scelto)' : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => handlePreview(template)} disabled={busy !== null}>
                    <EyeIcon className="h-4 w-4 mr-1" />
                    {busy === `preview-${template.id}` ? 'Generazione...' : 'Anteprima PDF'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDownload(template)} disabled={busy !== null}>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Word
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(template)} disabled={busy !== null}>
                    Modifica
                  </Button>
                  {!template.is_default && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleMakeDefault(template)} disabled={busy !== null}>
                        <StarIcon className="h-4 w-4 mr-1" />
                        Predefinito
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setToDelete(template)} disabled={busy !== null} title="Elimina">
                        <TrashIcon className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-1">
                {template.placeholders.length === 0 && (
                  <span className="text-xs text-amber-700">Nessun segnaposto trovato: l'attestato sarà uguale per tutti.</span>
                )}
                {template.placeholders.map((name) => (
                  <span key={name} className="text-xs font-mono bg-gray-100 text-gray-700 rounded px-2 py-0.5">
                    {'{{'}{name}{'}}'}
                  </span>
                ))}
              </div>
              {template.unknown_placeholders.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-2">
                  Segnaposto non riconosciuti (resteranno scritti così nel PDF):{' '}
                  {template.unknown_placeholders.map((name) => `{{${name}}}`).join(', ')}
                </p>
              )}
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader title="Segnaposto disponibili" />
          <p className="text-sm text-gray-600 mb-3">
            Scrivi il segnaposto nel documento Word, con le doppie parentesi graffe, nel punto in cui deve comparire
            il dato. Il testo inserito mantiene la formattazione del segnaposto (carattere, dimensione, colore).
          </p>
          <ul className="divide-y divide-gray-100">
            {placeholders.map((placeholder) => (
              <li key={placeholder.name} className="py-2">
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm font-mono text-primary-700">{placeholder.tag}</code>
                  <button
                    onClick={() => copyTag(placeholder.tag)}
                    className="text-xs text-gray-500 hover:text-primary-600 inline-flex items-center"
                    title="Copia"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                    {copied === placeholder.tag ? 'Copiato' : 'Copia'}
                  </button>
                </div>
                <p className="text-xs text-gray-600">{placeholder.description}</p>
                <p className="text-xs text-gray-400">Es. {placeholder.example}</p>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Modifica modello' : 'Carica modello attestato'}
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="es. Attestato di partecipazione"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {editing ? 'Sostituisci il file Word (facoltativo)' : 'File Word (.docx) *'}
              </label>
              <input
                type="file"
                accept=".docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="mt-1 text-xs text-gray-500">
                Solo formato .docx (Word 2007 o successivo), massimo 10 MB. Gli attestati già rilasciati non cambiano.
              </p>
            </div>
            {!(editing && editing.is_default) && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Modello predefinito (per i corsi senza un modello scelto)</span>
              </label>
            )}
            {formError && <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{formError}</p>}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowModal(false)}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : editing ? 'Salva' : 'Carica'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!toDelete} onClose={() => setToDelete(null)} title="Elimina modello">
        <ModalBody>
          <p className="text-gray-700">
            Eliminare il modello <strong>{toDelete?.name}</strong>?
            {toDelete && toDelete.courses_count > 0 && (
              <> I {toDelete.courses_count} corsi che lo usano passeranno al modello predefinito.</>
            )}{' '}
            Gli attestati già rilasciati restano scaricabili.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setToDelete(null)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={busy !== null}>
            Elimina
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
